import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { getProjectById } from "@/lib/journey.functions";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Clock,
  Lock,
  Trophy,
  Archive,
  Eye,
} from "lucide-react";
import {
  computeStageProgress,
  computeProjectProgress,
  deriveMissionState,
  isExecutable,
  type MissionState,
} from "@/lib/mission-status";

export const Route = createFileRoute(
  "/_authenticated/membros/meu-projeto/projeto/$id",
)({
  head: () => ({
    meta: [
      { title: "Histórico do projeto — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-lg rounded-2xl border border-black/5 bg-white p-8 text-center">
      <p className="font-display text-lg font-extrabold text-[var(--navy)]">
        Não foi possível carregar este projeto
      </p>
      <p className="mt-2 text-sm text-[var(--gray-ink)]">{error?.message}</p>
      <button
        onClick={reset}
        className="mt-4 rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
      >
        Tentar novamente
      </button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-lg rounded-2xl border border-black/5 bg-white p-8 text-center">
      <p className="font-display text-lg font-extrabold text-[var(--navy)]">
        Projeto não encontrado
      </p>
      <Link
        to="/membros/meu-projeto"
        className="mt-4 inline-block rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
      >
        Voltar
      </Link>
    </div>
  ),
  component: ProjectHistoryPage,
});

function ProjectHistoryPage() {
  const { id } = useParams({ from: "/_authenticated/membros/meu-projeto/projeto/$id" });
  const fetchProject = useServerFn(getProjectById);
  const { data, isLoading } = useQuery({
    queryKey: ["project-history", id],
    queryFn: () => fetchProject({ data: { projectId: id } }),
  });
  const [openStage, setOpenStage] = useState<string | null>(null);

  const derived = useMemo(() => {
    if (!data) return null;
    const { stages, missions, tasks, taskProgress, missionProgress } = data;
    const doneTaskIds = new Set(
      (taskProgress ?? []).filter((t: any) => t.completed_at).map((t: any) => t.task_id),
    );
    const mpMap = new Map<string, any>(
      (missionProgress ?? []).map((m: any) => [m.mission_id, m]),
    );
    const answersMap = new Map<string, any>(
      (taskProgress ?? []).map((t: any) => [t.task_id, t]),
    );

    // Same canonical rule used in Meu Projeto — history mirrors execution.
    const stageBlocks = (stages ?? []).map((s: any) => {
      const stageMissions = (missions ?? [])
        .filter((m: any) => m.stage_id === s.id)
        .map((m: any) => {
          const mTasks = (tasks ?? []).filter((t: any) => t.mission_id === m.id);
          const doneCount = mTasks.filter((t: any) => doneTaskIds.has(t.id)).length;
          const state: MissionState = deriveMissionState(m, mpMap);
          const mp = mpMap.get(m.id);
          return {
            ...m,
            taskCount: mTasks.length,
            doneCount,
            pct: Number(mp?.progress_pct) || (state === "completed" ? 100 : 0),
            completed: state === "completed",
            locked: state === "locked",
            executable: isExecutable(m),
            state,
            tasks: mTasks,
          };
        });
      const stageStats = computeStageProgress(stageMissions, mpMap);
      return {
        ...s,
        missions: stageMissions,
        pct: stageStats.pct,
        completed: stageStats.completed,
      };
    });
    const projectStats = computeProjectProgress(
      stageBlocks.flatMap((s: any) => s.missions),
      mpMap,
    );
    return { stageBlocks, doneTaskIds, answersMap, projectStats };
  }, [data]);

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-2xl bg-black/5" />;
  }
  if (!data) {
    return (
      <div className="rounded-2xl border border-black/5 bg-white p-8 text-center">
        <p className="text-[var(--gray-ink)]">Projeto não encontrado.</p>
      </div>
    );
  }
  const { project } = data;
  const business = (project as any).business;
  const isCompleted = project.status === "completed";
  const isArchived = project.status === "archived";

  return (
    <div className="space-y-6">
      <Link
        to="/membros/meu-projeto"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)]"
      >
        <ArrowLeft size={14} /> Voltar aos meus projetos
      </Link>

      <section className="rounded-3xl border border-black/5 bg-white p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
              isCompleted
                ? "bg-[var(--gold)]/10 text-[var(--gold)]"
                : isArchived
                  ? "bg-black/[0.06] text-[var(--gray-ink)]"
                  : "bg-[var(--green)]/10 text-[var(--green)]"
            }`}
          >
            {isCompleted ? <Trophy size={12} /> : isArchived ? <Archive size={12} /> : <Eye size={12} />}
            {isCompleted ? "Concluído" : isArchived ? "Arquivado" : "Em andamento"}
          </span>
          <span className="rounded-full border border-black/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Somente leitura
          </span>
        </div>

        <h1 className="mt-3 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
          {business?.title}
        </h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          {business?.category?.name} · Início em{" "}
          {new Date(project.started_at).toLocaleDateString("pt-BR")}
          {project.completed_at && (
            <>
              {" "}
              · Concluído em {new Date(project.completed_at).toLocaleDateString("pt-BR")}
            </>
          )}
          {project.archived_at && (
            <>
              {" "}
              · Arquivado em {new Date(project.archived_at).toLocaleDateString("pt-BR")}
            </>
          )}
        </p>

        <div className="mt-6 max-w-xl">
          <div className="mb-2 flex justify-between text-xs font-semibold text-[var(--gray-ink)]">
            <span>Progresso final</span>
            <span className="text-[var(--navy)]">
              {derived?.projectStats.pct ?? Number(project.progress_pct).toFixed(0)}%
            </span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-[var(--gray-soft)]">
            <div
              className="h-full rounded-full bg-[var(--green)]"
              style={{
                width: `${Math.min(100, derived?.projectStats.pct ?? Number(project.progress_pct))}%`,
              }}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">
          Cronograma executado
        </h2>
        {derived?.stageBlocks.map((s: any, idx: number) => {
          const isOpen = openStage === s.id || (openStage === null && idx === 0);
          return (
            <div key={s.id} className="overflow-hidden rounded-2xl border border-black/5 bg-white">
              <button
                onClick={() => setOpenStage(isOpen ? "__closed__" : s.id)}
                className="flex w-full items-center gap-4 p-5 text-left transition hover:bg-[var(--gray-soft)]/40"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-display text-sm font-extrabold ${
                    s.completed ? "bg-[var(--green)] text-white" : "bg-[var(--gray-soft)] text-[var(--gray-ink)]"
                  }`}
                >
                  {s.completed ? <CheckCircle2 size={16} /> : idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-base font-extrabold text-[var(--navy)]">
                    {s.title}
                  </p>
                  <div className="mt-1 flex items-center gap-3">
                    <div className="h-1.5 w-32 overflow-hidden rounded-full bg-[var(--gray-soft)]">
                      <div className="h-full rounded-full bg-[var(--green)]" style={{ width: `${s.pct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-[var(--gray-ink)]">{s.pct}%</span>
                  </div>
                </div>
                <ChevronDown size={16} className={`text-[var(--gray-ink)] transition ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="space-y-3 border-t border-black/5 p-5">
                  {s.missions.map((m: any) => (
                    <div key={m.id} className="rounded-xl border border-black/5 bg-white p-4">
                      <div className="flex items-center gap-2">
                        {m.locked ? (
                          <Lock size={14} className="text-[var(--gray-ink)]" />
                        ) : m.completed ? (
                          <CheckCircle2 size={14} className="text-[var(--green)]" />
                        ) : (
                          <Clock size={14} className="text-[var(--gray-ink)]" />
                        )}
                        <p className="text-sm font-semibold text-[var(--navy)]">{m.title}</p>
                        <span className="ml-auto text-xs text-[var(--gray-ink)]">
                          {m.doneCount}/{m.taskCount}
                        </span>
                      </div>
                      {m.tasks.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {m.tasks.map((t: any) => {
                            const done = derived.doneTaskIds.has(t.id);
                            const answer = derived.answersMap.get(t.id);
                            return (
                              <li key={t.id} className="rounded-lg border border-black/5 bg-[var(--gray-soft)]/30 p-2.5">
                                <div className="flex items-start gap-2 text-sm">
                                  <span
                                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-md border ${
                                      done ? "border-[var(--green)] bg-[var(--green)] text-white" : "border-black/20 bg-white"
                                    }`}
                                  >
                                    {done && <CheckCircle2 size={10} />}
                                  </span>
                                  <span className={done ? "text-[var(--gray-ink)] line-through" : "text-[var(--navy)]"}>
                                    {t.title}
                                  </span>
                                </div>
                                {(answer?.answer_text || answer?.note) && (
                                  <div className="mt-2 space-y-1 pl-6 text-xs">
                                    {answer.answer_text && (
                                      <p className="rounded-md bg-white p-2 text-[var(--navy)]">
                                        <strong>Resposta:</strong> {answer.answer_text}
                                      </p>
                                    )}
                                    {answer.note && (
                                      <p className="rounded-md bg-white p-2 italic text-[var(--gray-ink)]">
                                        Nota: {answer.note}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
